import { useEffect, useRef, useState } from "react";
import JsonEditor from "../../../CommonComponents/json_editor/JsonEditor";
import { showToastMessage } from "../../../../utils/commonFunctions";
import {
  InfoIcon,
  NoPreviewIcon,
  CaretIcon,
  CloseFadedIcon,
  CopyIcon,
  CopiedCheckIcon,
  PasteIcon,
  SideSheetIcon,
  JsonViewIcon,
  FormViewIcon,
  UploadIcon,
  DownloadIcon,
} from "./svgs/TestCaseSvgs";
import { TmState, ValidationRule, TestCaseStep, TelemetryEntry } from "./TestCaseTypes";
import {
  flattenPayload,
  flattenSchemaEnumMappings,
  flattenSchemaTypes,
  buildBackendTmPayload,
  exportValidationRule,
  importValidationRule,
  exportTemplateAsFile,
  parseTemplateFile,
  CopiedValidationConfig,
  CopiedValidationBundleConfig,
  ValidationClipboardConfig,
  isValidationConfigMatch,
  formatTreeHeaderLabel,
} from "./TestCaseHelpers";
import { SelectOption } from "./TestCaseSelectStyles";
import TmParamPanel from "./TelemetryParamPanel";
import TelecommandParamPanel from "./TelecommandParamPanel";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import InlineInput from "../../../CommonComponents/InlineInput/InlineInput";
import TCStepsPopup from "./TCStepsPopup";

interface PreviewPanelProps {
  testCaseSteps: TestCaseStep[];
  selectedCommand: SelectOption | null;
  busComponents: SelectOption[];
  tcJsonText: string;
  isJsonValid: boolean;
  selectedTelemetries: SelectOption[];
  paramStates: Record<string, TmState>;
  expandedTMs: string[];
  activePreviewTab: "json" | "validations" | "preview";
  activeTcPreviewTab: "json" | "form";
  testCaseName: string;
  onJsonChange: (text: string) => void;
  onTcTabChange: (tab: "json" | "form") => void;
  onToggleParam: (tmId: string, paramKey: string) => void;
  onUpdateRule: (tmId: string, paramKey: string, updates: Partial<ValidationRule>) => void;
  onRegisterArrayGroup: (tmId: string, group: { basePath: string; itemType: "primitive" | "object"; objectFields: string[]; indices: number[] }) => void;
  onToggleAccordion: (tmId: string) => void;
  onTabChange: (tab: "json" | "validations" | "preview") => void;
  onTestCaseNameChange: (name: string) => void;
  onShowSteps: () => void;
  onClearCommand: () => void;
  onParamStatesChange?: (states: Record<string, TmState>) => void;
}

const isValidationBundleConfig = (config: ValidationClipboardConfig): config is CopiedValidationBundleConfig =>
  config.type === "validation_config_bundle";

const getValidationScopeKey = (labels: string[]): string =>
  labels.length > 1 ? `selection:${labels.join("|")}` : labels[0] ?? "";

const sanitizeFilePart = (value: string): string =>
  value.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || "telemetry";

const PreviewPanel = ({
  testCaseSteps,
  selectedCommand,
  busComponents,
  tcJsonText,
  isJsonValid,
  selectedTelemetries,
  paramStates,
  expandedTMs,
  activePreviewTab,
  activeTcPreviewTab,
  testCaseName,
  onJsonChange,
  onTcTabChange,
  onToggleParam,
  onUpdateRule,
  onRegisterArrayGroup,
  onToggleAccordion,
  onTabChange,
  onTestCaseNameChange,
  onClearCommand,
  onParamStatesChange,
}: PreviewPanelProps): JSX.Element => {
  const [copied, setCopied] = useState(false);
  const [copiedValidationScope, setCopiedValidationScope] = useState<string | null>(null);
  const [showStepsPopup, setShowStepsPopup] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const templateFileInputRef = useRef<HTMLInputElement | null>(null);
  const templateLoadTargetRef = useRef<string[] | null>(null);
  const isValidationViewActive = activePreviewTab === "preview" || activePreviewTab === "validations";
  const selectedTmLabels = selectedTelemetries.map((tm) => tm.label);

  useEffect(() => {
    setShowStepsPopup(false);
  }, [testCaseSteps.length]);

  const handleCopy = (): void => {
    navigator.clipboard?.writeText(tcJsonText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getValidationConfigsForLabels = (labels: string[]): { configs: CopiedValidationConfig[]; skipped: string[] } => {
    const configs: CopiedValidationConfig[] = [];
    const skipped: string[] = [];

    labels.forEach((tmLabel) => {
      const tm = selectedTelemetries.find((item) => item.label === tmLabel);
      const tmState = paramStates[tmLabel];
      if (!tm || !tmState) {
        skipped.push(tmLabel);
        return;
      }
      configs.push(exportValidationRule(tmLabel, tm.value, tmState));
    });

    return { configs, skipped };
  };

  const createValidationBundle = (configs: CopiedValidationConfig[]): CopiedValidationBundleConfig => ({
    type: "validation_config_bundle",
    version: "1.0",
    createdAt: Date.now(),
    configs,
  });

  const downloadValidationConfig = (config: ValidationClipboardConfig, labels: string[]): { success: boolean; error?: string } => {
    try {
      const jsonStr = JSON.stringify(config, null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = labels.length > 1
        ? `validation-template-selection-${labels.length}-tm-${Date.now()}.json`
        : `validation-template-${sanitizeFilePart(labels[0])}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return { success: true };
    } catch {
      return { success: false, error: "Failed to save template" };
    }
  };

  const findConfigForTelemetry = (configs: CopiedValidationConfig[], tm: SelectOption): CopiedValidationConfig | undefined =>
    configs.find((config) => config.tmLabel === tm.label && isValidationConfigMatch(config, tm.value)) ??
    configs.find((config) => isValidationConfigMatch(config, tm.value));

  const applyValidationConfigToLabels = (config: ValidationClipboardConfig, labels: string[], source: "clipboard" | "template"): void => {
    if (!onParamStatesChange) {
      showToastMessage("error", "Unable to update validation rules");
      return;
    }

    const targets = labels
      .map((label) => selectedTelemetries.find((tm) => tm.label === label))
      .filter((tm): tm is SelectOption => Boolean(tm));

    if (targets.length === 0) {
      showToastMessage("error", "Select telemetry first");
      return;
    }

    const configs = isValidationBundleConfig(config) ? config.configs : [config];
    const nextStates = { ...paramStates };
    const applied: string[] = [];
    const skipped: string[] = [];
    const failures: string[] = [];

    targets.forEach((tm) => {
      const matchingConfig = isValidationBundleConfig(config) ? findConfigForTelemetry(configs, tm) : config;
      if (!matchingConfig) {
        skipped.push(tm.label);
        return;
      }

      const result = importValidationRule(matchingConfig, tm.value, paramStates[tm.label], "replace");
      if (!result.success) {
        failures.push(result.warning ?? tm.label);
        return;
      }

      nextStates[tm.label] = result.state;
      applied.push(tm.label);
    });

    if (applied.length === 0) {
      showToastMessage("error", failures[0] || "No matching validation rules found for selected telemetry");
      return;
    }

    onParamStatesChange(nextStates);

    if (targets.length === 1) {
      showToastMessage("success", `Validation rules ${source === "template" ? "loaded" : "applied"} for ${applied[0]}`);
      return;
    }

    const missedCount = targets.length - applied.length;
    showToastMessage(
      missedCount > 0 ? "info" : "success",
      missedCount > 0
        ? `Validation rules applied to ${applied.length} of ${targets.length} TMs`
        : `Validation rules applied to ${applied.length} TMs`
    );
  };

  const handleCopyValidationRules = (labels: string[]): void => {
    if (labels.length === 0) {
      showToastMessage("error", "Select telemetry first");
      return;
    }

    const { configs, skipped } = getValidationConfigsForLabels(labels);
    if (configs.length === 0) {
      showToastMessage("error", "No validation rules defined");
      return;
    }

    if (!navigator.clipboard?.writeText) {
      showToastMessage("error", "Clipboard is not available");
      return;
    }

    const payload: ValidationClipboardConfig = labels.length > 1 ? createValidationBundle(configs) : configs[0];
    navigator.clipboard.writeText(JSON.stringify(payload)).then(() => {
      const scopeKey = getValidationScopeKey(labels);
      setCopiedValidationScope(scopeKey);
      setTimeout(() => setCopiedValidationScope(null), 2000);
      showToastMessage(
        "success",
        labels.length > 1
          ? skipped.length > 0
            ? `Validation rules copied for ${configs.length} of ${labels.length} TMs`
            : `Validation rules copied for ${configs.length} TMs`
          : `Validation rules copied for ${labels[0]}`
      );
    }).catch(() => {
      showToastMessage("error", "Failed to copy to clipboard");
    });
  };

  const handlePasteValidationRules = (labels: string[]): void => {
    if (!navigator.clipboard?.readText) {
      showToastMessage("error", "Clipboard is not available");
      return;
    }

    navigator.clipboard.readText().then((text) => {
      const parseResult = parseTemplateFile(text);
      if (!parseResult.success || !parseResult.config) {
        showToastMessage("error", "Invalid clipboard format. Expected validation config.");
        return;
      }
      applyValidationConfigToLabels(parseResult.config, labels, "clipboard");
    }).catch(() => {
      showToastMessage("error", "Failed to read clipboard");
    });
  };

  const handleSaveAsTemplate = (labels: string[]): void => {
    const { configs, skipped } = getValidationConfigsForLabels(labels);
    if (configs.length === 0) {
      showToastMessage("error", "Cannot save template without validation rules");
      return;
    }

    if (labels.length === 1) {
      const tm = selectedTelemetries.find((item) => item.label === labels[0]);
      const result = tm ? exportTemplateAsFile(labels[0], tm.value, paramStates[labels[0]]) : { success: false, error: "Telemetry not found" };
      if (result.success) {
        showToastMessage("success", "Template saved successfully");
      } else {
        showToastMessage("error", result.error || "Failed to save template");
      }
      return;
    }

    const result = downloadValidationConfig(createValidationBundle(configs), labels);
    if (result.success) {
      showToastMessage(
        "success",
        skipped.length > 0
          ? `Template saved for ${configs.length} of ${labels.length} TMs`
          : `Template saved for ${configs.length} TMs`
      );
    } else {
      showToastMessage("error", result.error || "Failed to save template");
    }
  };

  const handleLoadTemplate = (labels: string[]): void => {
    templateLoadTargetRef.current = labels;
    templateFileInputRef.current?.click();
  };

  const renderValidationActionButtons = (labels: string[], placement: "top" | "bottom"): JSX.Element => {
    const isCopiedScope = copiedValidationScope === getValidationScopeKey(labels);
    const scopeSuffix = labels.length > 1 ? " for selected telemetry" : "";

    return (
      <>
        <CustomToolTip title={`Copy validation rules${scopeSuffix}`} placement={placement}>
          <button
            type="button"
            className={`tcd-tm-corner-btn${isCopiedScope ? " tcd-tm-corner-btn--copied" : ""}`}
            onClick={() => handleCopyValidationRules(labels)}
          >
            {isCopiedScope ? <CopiedCheckIcon /> : <CopyIcon />}
          </button>
        </CustomToolTip>
        <CustomToolTip title={`Paste validation rules${scopeSuffix}`} placement={placement}>
          <button
            type="button"
            className="tcd-tm-corner-btn"
            onClick={() => handlePasteValidationRules(labels)}
          >
            <PasteIcon />
          </button>
        </CustomToolTip>
        <CustomToolTip title={`Save template file${scopeSuffix}`} placement={placement}>
          <button
            type="button"
            className="tcd-tm-corner-btn"
            onClick={() => handleSaveAsTemplate(labels)}
          >
            <DownloadIcon />
          </button>
        </CustomToolTip>
        <CustomToolTip title={`Load template file${scopeSuffix}`} placement={placement}>
          <button
            type="button"
            className="tcd-tm-corner-btn"
            onClick={() => handleLoadTemplate(labels)}
          >
            <UploadIcon />
          </button>
        </CustomToolTip>
      </>
    );
  };

  if (!selectedCommand && testCaseSteps.length === 0) {
    return (
      <div className="tcd-right-panel">
        <div className="tcd-no-preview-icon"><NoPreviewIcon /></div>
        <div className="tcd-no-preview-title">No Preview</div>
        <div className="tcd-no-preview-sub">Select TC &amp; TM to edit Validation</div>
      </div>
    );
  }

  return (
    <div className="tcd-preview">
      {/* Header */}
      <div className="tcd-preview__header">
        <div className="tcd-preview__header-left">
          <span className="tcd-preview__title">PREVIEW</span>
          {(testCaseSteps.length > 0 || selectedCommand) && (
            <InlineInput
              placeholder="Enter Test Case Title"
              value={testCaseName}
              onChange={(value) => onTestCaseNameChange(value)}
              onBlur={() => {
                const trimmed = testCaseName.trim();
                if (trimmed) {
                  onTestCaseNameChange(trimmed);
                }
                setIsEditingName(false);
              }}
              showTooltip={true}
              tooltipTitle={testCaseName}
              tooltipPlacement='top-start'
            />
          )}
        </div>
        <div className="tcd-preview__header-right">
          <span className="tcd-preview__tc-badge" onClick={() => setShowStepsPopup(true)}>
            <SideSheetIcon />
            TC <span className="tcd-preview__tc-count">{testCaseSteps.length}</span>
          </span>
          {selectedCommand && (
            <button className="tcd-preview__close" type="button" onClick={onClearCommand} title="Close command">
              <CloseFadedIcon />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {!selectedCommand ? (
        <div className="tcd-preview__body tcd-preview__body--empty">
          <div className="tcd-no-preview-icon"><NoPreviewIcon /></div>
          <div className="tcd-no-preview-title">No Preview</div>
          <div className="tcd-no-preview-sub">Select TC &amp; TM to edit Validation</div>
        </div>
      ) : (
        <div className="tcd-preview__body">
          {/* TC Section */}
          <div className="tcd-tc-section">
            <div className="tcd-tc-section__header">
              <div className="tcd-tc-section__title">
                <span>TELECOMMAND</span>
              </div>
              <div className="tcd-tc-section__actions">
                <span className={`tcd-json-status${isJsonValid ? " valid" : " invalid"}`}>
                  {isJsonValid ? "✓ Valid JSON" : "✕ Invalid JSON"}
                </span>
                <button className={`tcd-icon-btn${copied ? " tcd-icon-btn--copied" : ""}`} type="button" title={copied ? "Copied!" : "Copy JSON"} onClick={handleCopy}>
                  {copied ? (
                    <>
                      <CopiedCheckIcon />
                      <span className="tcd-icon-btn__label">Copied!</span>
                    </>
                  ) : (
                    <CopyIcon />
                  )}
                </button>
                <div className="tcd-tc-tabs">
                  <button type="button" className={`tcd-tc-tab ${activeTcPreviewTab === "json" ? "active" : ""}`} onClick={() => onTcTabChange("json")} title="JSON view">
                    <span className="tcd-tc-tab__icon"><FormViewIcon /></span>
                  </button>
                  <button type="button" className={`tcd-tc-tab ${activeTcPreviewTab === "form" ? "blue" : ""}`} onClick={() => onTcTabChange("form")} title="Form view">
                    <span className="tcd-tc-tab__icon"><JsonViewIcon /></span>
                  </button>
                </div>
              </div>
            </div>
            {activeTcPreviewTab === "json" ? (
              <div className={`tcd-json-editor-wrap${!isJsonValid ? " invalid" : ""}`}>
                <JsonEditor value={tcJsonText} editable={true} onChange={(val) => onJsonChange(val ?? "")} backgroundColor="rgba(9, 9, 9, 0.65)" />
              </div>
            ) : (
              <div className="tcd-tc-form-wrap">
                <TelecommandParamPanel tcJsonText={tcJsonText} onUpdateJson={onJsonChange} />
              </div>
            )}
          </div>

          {/* TM Section */}
          <div className="tcd-tm-section">
            <div className="tcd-tm-section__header">
              <span className="tcd-tm-section__title">TELEMETRY</span>
              <div className="tcd-tm-tabs">
                <button type="button" className={`tcd-tm-tab${activePreviewTab === "json" ? " active" : ""}`} onClick={() => onTabChange("json")}>
                  <span className="tcd-tm-tab__icon"><FormViewIcon /></span>
                  TM JSON
                </button>
                <div className={`tcd-tm-validation-toggle${isValidationViewActive ? " active" : ""}`}>
                  <button
                    type="button"
                    className="tcd-tm-validation-toggle__label"
                    onClick={() => onTabChange("validations")}
                    disabled={isValidationViewActive}
                  >
                    Validation
                  </button>
                  <CustomToolTip title="JSON preview" placement="top">
                    <button
                      type="button"
                      className={`tcd-tm-view-btn${activePreviewTab === "preview" ? " active" : ""}`}
                      onClick={() => onTabChange("preview")}
                    >
                      <FormViewIcon />
                    </button>
                  </CustomToolTip>
                  <CustomToolTip title="Rule builder" placement="top">
                    <button
                      type="button"
                      className={`tcd-tm-view-btn${activePreviewTab === "validations" ? " active" : ""}`}
                      onClick={() => onTabChange("validations")}
                    >
                      <JsonViewIcon />
                    </button>
                  </CustomToolTip>
                </div>
              </div>
            </div>

            <div className={`tcd-tm-content${activePreviewTab === "validations" && selectedTelemetries.length === 1 ? " tcd-tm-content--single-builder" : ""}`}>
              {activePreviewTab === "json" ? (
                <div
                  className="tcd-tm-json-editor-wrap"
                  onKeyDownCapture={(e) => {
                    const isEditKey =
                      e.key === "Backspace" ||
                      e.key === "Delete" ||
                      e.key === "Enter" ||
                      (e.key.length === 1 && !e.ctrlKey && !e.metaKey);

                    if (isEditKey) {
                      showToastMessage("info", "Editing is disabled for TM JSON");
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <JsonEditor
                    value={selectedTelemetries.length === 0 ? "// No telemetry selected" : JSON.stringify(selectedTelemetries.map((t) => t.value), null, 2)}
                    editable={false}
                    backgroundColor="rgba(9, 9, 9, 0.65)"
                  />
                </div>
              ) : activePreviewTab === "preview" ? (
                <div className={`tcd-tm-json-editor-wrap${selectedTelemetries.length === 0 ? " tcd-tm-json-editor-wrap--empty-strip" : ""}`}>
                  {selectedTelemetries.length === 0 ? (
                    <div className="tcd-tm-empty"><InfoIcon /><span>Select telemetry to preview validation rules as JSON</span></div>
                  ) : (
                    <JsonEditor
                      value={JSON.stringify(
                        selectedTelemetries
                          .map((t) => {
                            const enumMappings = flattenSchemaEnumMappings((t.value as TelemetryEntry)?.payloadSchema);
                            const paramTypes = flattenSchemaTypes((t.value as TelemetryEntry)?.payloadSchema);
                            return buildBackendTmPayload(paramStates, t.label, t.value, enumMappings, paramTypes);
                          })
                          .filter(Boolean),
                        null,
                        2
                      )}
                      editable={false}
                      backgroundColor="rgba(9, 9, 9, 0.65)"
                    />
                  )}
                </div>
              ) : selectedTelemetries.length === 0 ? (
                <div className="tcd-tm-empty">
                  <InfoIcon />
                  <span>Select telemetry from the left panel to configure validations</span>
                </div>
              ) : selectedTelemetries.length === 1 ? (
                <div className="tcd-tm-single-wrap">
                  <div className="tcd-tm-corner-actions">
                    {renderValidationActionButtons([selectedTelemetries[0].label], "top")}
                  </div>
                  <TmParamPanel
                    params={flattenPayload(
                      (selectedTelemetries[0].value as { payload?: Record<string, unknown> })?.payload ?? {},
                      "",
                      (selectedTelemetries[0].value as TelemetryEntry)?.payloadSchema
                    )}
                    state={paramStates[selectedTelemetries[0].label]}
                    onToggle={(key) => onToggleParam(selectedTelemetries[0].label, key)}
                    onUpdateRule={(key, updates) => onUpdateRule(selectedTelemetries[0].label, key, updates)}
                    onRegisterArrayGroup={(group) => onRegisterArrayGroup(selectedTelemetries[0].label, group)}
                    enumMappings={flattenSchemaEnumMappings((selectedTelemetries[0].value as TelemetryEntry)?.payloadSchema)}
                    tooltipPosition="left"
                    showScroll={true}
                  />
                </div>
              ) : (
                <div className="tcd-tm-multi-wrap">
                  <div className="tcd-tm-global-actions-bar">
                    <span className="tcd-tm-global-actions-bar__label">{selectedTelemetries.length} TM selected</span>
                    <div className="tcd-tm-corner-actions tcd-tm-corner-actions--inline">
                      {renderValidationActionButtons(selectedTmLabels, "bottom")}
                    </div>
                  </div>
                  {selectedTelemetries.map((tm) => (
                    <div key={tm.label} className="tcd-tm-accordion">
                      <div
                        className={`tcd-tm-accordion__header${expandedTMs.includes(tm.label) ? " expanded" : ""}`}
                        onClick={() => onToggleAccordion(tm.label)}
                      >
                        <span className="tcd-tm-accordion__caret">
                          <CaretIcon expanded={expandedTMs.includes(tm.label)} />
                        </span>
                        <span>{formatTreeHeaderLabel(tm.label)}</span>
                      </div>
                      {expandedTMs.includes(tm.label) && (
                        <TmParamPanel
                          params={flattenPayload((tm.value as { payload?: Record<string, unknown> })?.payload ?? {}, "", (tm.value as TelemetryEntry)?.payloadSchema)}
                          state={paramStates[tm.label]}
                          onToggle={(key) => onToggleParam(tm.label, key)}
                          onUpdateRule={(key, updates) => onUpdateRule(tm.label, key, updates)}
                          onRegisterArrayGroup={(group) => onRegisterArrayGroup(tm.label, group)}
                          enumMappings={flattenSchemaEnumMappings((tm.value as TelemetryEntry)?.payloadSchema)}
                          tooltipPosition="left"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <input
        ref={templateFileInputRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const targetLabels = templateLoadTargetRef.current ?? selectedTelemetries.map((tm) => tm.label);
          if (targetLabels.length === 0) {
            showToastMessage("error", "Select a telemetry first");
            return;
          }
          const reader = new FileReader();
          reader.onload = (evt) => {
            try {
              const content = evt.target?.result as string;
              const parseResult = parseTemplateFile(content);
              if (!parseResult.success || !parseResult.config) {
                showToastMessage("error", parseResult.error || "Failed to load template");
                return;
              }
              applyValidationConfigToLabels(parseResult.config, targetLabels, "template");
            } catch (err) {
              showToastMessage("error", "Error reading template file");
            } finally {
              templateLoadTargetRef.current = null;
            }
          };
          reader.readAsText(file);
          e.target.value = "";
        }}
      />

      <TCStepsPopup
        show={showStepsPopup}
        onClose={() => setShowStepsPopup(false)}
        testCaseSteps={testCaseSteps}
        selectedCommand={selectedCommand}
        busComponents={busComponents}
        title={testCaseName}
      />
    </div>
  );
};

export default PreviewPanel;
